import {ChangeEvent, FocusEvent} from 'react';
import {store} from 'src/app/store';
import {
    ChargePointStatus,
    ChargingStatusEnum,
    ConfigurationStatusEnum,
    ConnectorStatusEnum,
    CurrencyEnum,
} from 'src/enums';
import {AuthUserDTO} from '../auth';
import {TransactionCostDTO} from '../transactions';

export type AppDispatch = typeof store.dispatch;

export interface CommonStateType {
    lang: string;
}

export interface isSubmittingProps {
    setSubmitting: (isSub: boolean) => void;
}

export interface FormKeyValue {
    [key: string]: string;
}

export interface PageInfo {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
}

export interface Page {
    page: PageInfo;
}

export interface ItemCostUpdate {
    businessId: number;
    currency: CurrencyEnum;
    initCost?: number;
    installationDate: number;
    name?: string;
    serviceLife: number;
}

export interface ItemCostCreate {
    businessId: number;
    currency: CurrencyEnum;
    initCost?: number;
    name?: string;
    installationDate: number;
    serviceLife: number;
}

export interface ItemCost {
    chargePointId: number;
    currency: CurrencyEnum;
    currentCost: number;
    id: number;
    initCost: number;
    installationDate: number;
    locationId: number;
    name: string;
    serviceLife: number;
}

export interface FormInterface {
    handleBlur: (e: FocusEvent<any>) => void;
    handleChange: (e: string | ChangeEvent<any>) => void;
    handleSubmit: () => void;
    isSubmitting: boolean;
    errors: FormKeyValue;
    values: FormKeyValue;
    touched: FormKeyValue;
    setFieldValue: any;
    setTouched: any;
}

export interface SelectValue {
    value: string | number;
    id: number;
    name: string;
    onClick?: () => void;
}

export interface AutocompleteValue {
    id: number;
    label: string;
    caption?: string;
    image?: string;
    index?: number;
}

export interface DefaultAuthResponse {
    message: string;
    payload: AuthUserDTO;
    success: boolean;
}

export interface DefaultResponse {
    message: string;
    payload: string;
    success: boolean;
    error?: string;
}

export enum FeedbackTypeEnum {
    CHANGE_TARIFF = 'CHANGE_TARIFF',
    HELP = 'HELP',
    NEW_RFID = 'NEW_RFID',
}

export interface DefaultError {
    error: ErrorObject;
}

export interface ErrorObject extends Error {
    data: DefaultResponse;
    status: number;
}

export interface OCPPTagDTO {
    blocked: boolean;
    created: string;
    id: number;
    idTag: string;
    licensePlate?: string | null;
}

export interface MinimalOCPPTagDTO {
    id: number;
    idTag: string;
    blocked: boolean;
    isDefault: boolean;
    created: string;
    licensePlate?: string | null;
}

export interface SearchRequestParams {
    size?: number;
    page?: number;
}

export interface LatLngType {
    lat: number;
    lng: number;
}

export interface DataType {
    [index: string]: any;
}

export interface ChartSimpleData {
    count: number;
    date: string;
}

export interface CHARGE_POINT_STATUS_TYPE {
    chargerId: number;
    chargePointId: string;
    status: ChargePointStatus;
}

export interface CHARGING_STATUS_TYPE {
    amperage: string;
    chargerId: number;
    chargePointId: string;
    status: ChargingStatusEnum;
    /** The connector's **database id**, not the socket number printed on the station. */
    connectorId: number;
    transactionId: number;
    connectedTimeMinutes: number;
    chargingTimeMinutes: number;
    wattsConsumed: number;
    /**
     * The card the session runs on, the person the energy is billed to, and what it has cost so far.
     *
     * All three have always been in the message and nothing here declared them, so a screen watching
     * a live session had to ask `/transactions/active` for facts it had already been told - and the
     * price stood still between those asks.
     */
    idTag?: MinimalOCPPTagDTO;
    userId?: number;
    cost?: TransactionCostDTO;
}

export interface CONNECTOR_STATUS_TYPE {
    chargerPointId: string;
    chargerId: number;
    chargePointId: string;
    /** The connector's **database id**, not the socket number printed on the station. */
    connectorId: number;
    status: ConnectorStatusEnum;
}

export interface CONFIGURATIONS_TYPE {
    chargerId: number;
    chargePointId: string;
    status: ConfigurationStatusEnum;
    configurations: FormKeyValue[];
}

export interface AMPERAGE_TYPE {
    transactionId: number;
    chargerId: number;
    chargePointId: string;
    amperage: number;
}

export interface TableParamsType {
    page?: number;
    size?: number;
}

export enum NotificationTypeEnum {
    CHANGE_PLAN_REQUEST = 'CHANGE_PLAN_REQUEST',
    NEW_CONTRACT_EXIST_LOCATION = 'NEW_CONTRACT_EXIST_LOCATION',
    NEW_CONTRACT_NEW_LOCATION = 'NEW_CONTRACT_NEW_LOCATION',
    USER_FEEDBACK = 'USER_FEEDBACK',
    NEW_USER_REGISTRATION = 'NEW_USER_REGISTRATION',
    STATION_ASSOCIATION = 'STATION_ASSOCIATION',
    NEW_RFID_REQUEST = 'NEW_RFID_REQUEST',
    NEW_LOCATION = 'NEW_LOCATION',
    NEW_COMPANY_REGISTRATION_REQUEST = 'NEW_COMPANY_REGISTRATION_REQUEST',
}

export interface ListParamsType {
    page: number;
    size: number;
}
